import { HttpClient, type HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { type Observable, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { Airline } from '@fcom/common/interfaces/airlines';
import { fileToBase64 } from '@fcom/common/utils/form-utils';
import { type AppState, ConfigService, SentryLogger } from '@fcom/core';
import { loginToken } from '@fcom/core/selectors';
import { formatFinnairPlusCardNumber } from '@fcom/core/utils';
import { type Attachment } from '@fcom/ui-components/components/interfaces';
import { LanguageService } from '@fcom/ui-translate';

import {
  ClaimFlightsMissingAviosFormData,
  ClaimFlightsMissingAviosSubmissionData,
  ClaimMissingAviosResponseData,
  ClaimMissingPartnerPointsFormData,
  ClaimMissingPartnerPointsFormField,
  ClaimMissingPointsFormOrigin,
  ClaimPartnerMissingAviosSubmissionData,
  LoggedInMissingPointsErrorCode,
  LoggedInMissingPointsFormField,
  LoggedInMissingPointsResponseData,
  LoggedInMissingPointsSubmissionData,
  LoggedOutMissingPointsFormField,
  LoggedOutMissingPointsSubmissionData,
  PortalServicesMailerResponseData,
} from '../interfaces';
import { SubmitService } from '../services/submit.service';

type HttpPostOptions = Parameters<HttpClient['post']>[2];

@Injectable()
export class MissingPointsService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private sentryLogger: SentryLogger,
    private submitService: SubmitService,
    private store$: Store<AppState>,
    private languageService: LanguageService
  ) {}

  loggedInMissingPointsRequest(
    formValues: LoggedInMissingPointsSubmissionData
  ): Observable<LoggedInMissingPointsResponseData> {
    const params = this.getSubmissionData(formValues);
    return this.missingPointsRequest(params);
  }

  loggedOutMissingPointsRequest(
    formValues: LoggedOutMissingPointsSubmissionData
  ): Observable<PortalServicesMailerResponseData> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.portalServices;
    const httpOptions = {
      headers: new HttpHeaders({
        'x-api-key': keyId,
      }),
    };

    return this.http
      .post<PortalServicesMailerResponseData>(
        `${apiUrl}/mailer`,
        this.getSubmissionDataForLoggedOutMissingPoints(formValues),
        httpOptions
      )
      .pipe(
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((error: unknown): Observable<never> => {
          this.sentryLogger.warn('Error submitting logged out MissingPoints request', { error });
          return throwError(() => error);
        })
      );
  }

  public formatTicketNumber(ticketNumber: string): string {
    return ticketNumber.replace(/-/g, '');
  }

  private getSubmissionDataForLoggedOutMissingPoints(formValues: LoggedOutMissingPointsSubmissionData): FormData {
    const flightFrom = formValues[LoggedOutMissingPointsFormField.FLIGHT_FROM];
    const flightTo = formValues[LoggedOutMissingPointsFormField.FLIGHT_TO];

    const data = new FormData();
    const attachment: Attachment = formValues[LoggedOutMissingPointsFormField.ATTACHMENT];
    const file: File = Array.isArray(attachment) ? attachment.at(0)?.file : attachment?.file;

    data.append('__addressKey', 'emailServlet.plusMissingPoints');
    data.append('__messageSubject', 'Missing points form');
    data.append('__from', formValues[LoggedOutMissingPointsFormField.EMAIL]);
    data.append('__confirmationTo', formValues[LoggedOutMissingPointsFormField.EMAIL]);
    data.append('__locale', this.languageService.langKeyValue);

    data.append('01___passenger_name', formValues[LoggedOutMissingPointsFormField.PASSENGER_NAME]);
    data.append(
      '02___plus_membership_number',
      formatFinnairPlusCardNumber(formValues[LoggedOutMissingPointsFormField.MEMBER_NUMBER])
    );
    if (formValues[LoggedOutMissingPointsFormField.TICKET_NUMBER]) {
      data.append('03___ticket_number', formValues[LoggedOutMissingPointsFormField.TICKET_NUMBER]);
    }
    data.append('04___flight_from', flightFrom && `${flightFrom.title} (${flightFrom.locationCode})`);
    data.append('05___flight_to', flightTo && `${flightTo.title} (${flightTo.locationCode})`);
    data.append('06___departure_date', formValues[LoggedOutMissingPointsFormField.DEPARTURE_DATE].toDottedUIString());
    if (formValues[LoggedOutMissingPointsFormField.RETURN_DATE]) {
      data.append('06___return_date', formValues[LoggedOutMissingPointsFormField.RETURN_DATE].toDottedUIString());
    }
    Object.entries(this.transformFlightNumbers(formValues[LoggedOutMissingPointsFormField.FLIGHT_NUMBERS])).forEach(
      ([key, value]) => data.append(key, value)
    );
    data.append('08___travel_companion', formValues[LoggedOutMissingPointsFormField.COMPANIONS].length > 0 ? '1' : '0');
    Object.entries(this.transformCompanions(formValues[LoggedOutMissingPointsFormField.COMPANIONS])).forEach(
      ([key, value]) => data.append(key, value)
    );
    if (formValues[LoggedOutMissingPointsFormField.MESSAGE]) {
      data.append('10___message', formValues[LoggedOutMissingPointsFormField.MESSAGE]);
    }
    data.append('11___email', formValues[LoggedOutMissingPointsFormField.EMAIL]);
    data.append('12___phone', formValues[LoggedOutMissingPointsFormField.PHONE]);
    data.append('13___airline', formValues[LoggedOutMissingPointsFormField.AIRLINE]);
    if (formValues[LoggedOutMissingPointsFormField.BOOKING_CLASS]) {
      data.append('14___bookingClass', formValues[LoggedOutMissingPointsFormField.BOOKING_CLASS]);
    }
    data.append('attachment', file);

    return data;
  }

  private transformFlightNumbers(items): { [key: string]: string } {
    if (items.length === 0) {
      return {};
    }
    return items
      .map((flight: object, index: number) => {
        const key = `07___flight_number_${index + 1}`;

        return {
          [key]: flight[LoggedOutMissingPointsFormField.FLIGHT_NUMBER],
        };
      })
      .reduce((rest, curr) => ({ ...rest, ...curr }));
  }

  private transformCompanions(items): { [key: string]: string } {
    if (items.length === 0) {
      return {};
    }
    return items
      .map((companion: object, index: number) => {
        const nameKey = `09___companion_${index + 1}_name`;
        const memberNumberKey = `09___companion_${index + 1}_plus_number`;

        return {
          [nameKey]: companion[LoggedOutMissingPointsFormField.COMPANION_NAME],
          [memberNumberKey]: formatFinnairPlusCardNumber(
            companion[LoggedOutMissingPointsFormField.COMPANION_MEMBER_NUMBER]
          ),
        };
      })
      .reduce((rest, curr) => ({ ...rest, ...curr }));
  }

  private getSubmissionData(formValues: LoggedInMissingPointsSubmissionData): { [key: string]: string } {
    return {
      [LoggedInMissingPointsFormField.AIRLINE]: formValues[LoggedInMissingPointsFormField.AIRLINE],
      [LoggedInMissingPointsFormField.ARRIVAL_AIRPORT]: formValues[LoggedInMissingPointsFormField.ARRIVAL_AIRPORT]
        ? formValues[LoggedInMissingPointsFormField.ARRIVAL_AIRPORT].locationCode
        : undefined,
      [LoggedInMissingPointsFormField.BOOKING_CLASS]: formValues[LoggedInMissingPointsFormField.BOOKING_CLASS],
      [LoggedInMissingPointsFormField.DEPARTURE_AIRPORT]: formValues[LoggedInMissingPointsFormField.DEPARTURE_AIRPORT]
        ? formValues[LoggedInMissingPointsFormField.DEPARTURE_AIRPORT].locationCode
        : undefined,
      [LoggedInMissingPointsFormField.FLIGHT_DATE]:
        formValues[LoggedInMissingPointsFormField.FLIGHT_DATE] &&
        formValues[LoggedInMissingPointsFormField.FLIGHT_DATE].toString(),
      [LoggedInMissingPointsFormField.FLIGHT_NUMBER]: formValues[LoggedInMissingPointsFormField.FLIGHT_NUMBER],
      [LoggedInMissingPointsFormField.SEQUENCE_NUMBER]: formValues[LoggedInMissingPointsFormField.SEQUENCE_NUMBER],
      [LoggedInMissingPointsFormField.TICKET_NUMBER]: this.formatTicketNumber(
        formValues[LoggedInMissingPointsFormField.TICKET_NUMBER]
      ),
    };
  }

  private missingPointsRequest(data: { [key: string]: string }): Observable<LoggedInMissingPointsResponseData> {
    const isPartner = data[LoggedInMissingPointsFormField.AIRLINE] !== Airline.FINNAIR;
    const apiUrl = `${this.configService.cfg.serviceForm.loggedInMissingPoints.apiUrl}${isPartner ? '/partner' : ''}`;
    const keyId = this.configService.cfg.serviceForm.loggedInMissingPoints.keyId;
    return this.store$.pipe(
      loginToken(),
      take(1),
      switchMap((token: string) => {
        const httpOptions = {
          // eslint-disable-next-line camelcase
          headers: this.submitService.buildHeaders({ 'x-api-key': keyId, oauth_token: token }),
        };

        return this.http.post<LoggedInMissingPointsResponseData>(apiUrl, data, httpOptions).pipe(
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          catchError((err: HttpErrorResponse) => {
            if (
              !err.error.error?.errorCode ||
              err.error.error.errorCode === LoggedInMissingPointsErrorCode.INTERNAL_ERROR
            ) {
              this.sentryLogger.warn('Error submitting logged in MissingPoints request', { error: err });
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  claimFlightsMissingAvios = (
    claimMissingAviosFormData: ClaimFlightsMissingAviosFormData
  ): Observable<ClaimMissingAviosResponseData> => {
    const attachment: Attachment | Attachment[] = claimMissingAviosFormData.attachment;
    const file: File = Array.isArray(attachment) ? attachment[0]?.file : attachment?.file;
    const claimFlightsMissingAviosSubmissionData: ClaimFlightsMissingAviosSubmissionData =
      this.getFlightsMissingAviosSubmissionData(claimMissingAviosFormData);
    if (file) {
      return fileToBase64(file).pipe(
        switchMap((base64) => {
          claimFlightsMissingAviosSubmissionData.attachment = base64;
          claimFlightsMissingAviosSubmissionData.attachmentName = file.name;
          return this.sendToClaimMissingPoints(JSON.stringify(claimFlightsMissingAviosSubmissionData));
        })
      );
    } else {
      return this.sendToClaimMissingPoints(JSON.stringify(claimFlightsMissingAviosSubmissionData));
    }
  };

  claimPartnerMissingAvios = (
    claimMissingPartnerPointsFormData: ClaimMissingPartnerPointsFormData
  ): Observable<ClaimMissingAviosResponseData> => {
    const claimPartnerMissingAviosSubmissionData: ClaimPartnerMissingAviosSubmissionData = {
      claimMissingPointsForm: ClaimMissingPointsFormOrigin.FROM_PARTNERS,
      passengerName:
        claimMissingPartnerPointsFormData.claimMissingPartnerPoints[ClaimMissingPartnerPointsFormField.MEMBER_NAME],
      memberNumber:
        claimMissingPartnerPointsFormData.claimMissingPartnerPoints[ClaimMissingPartnerPointsFormField.MEMBER_NUMBER],
      email: claimMissingPartnerPointsFormData.claimMissingPartnerPoints[ClaimMissingPartnerPointsFormField.EMAIL],
      message:
        claimMissingPartnerPointsFormData.claimMissingPartnerPoints[
          ClaimMissingPartnerPointsFormField.ADDITIONAL_INFORMATION
        ],
      locale: this.languageService.langKeyValue,
    };
    const attachment =
      claimMissingPartnerPointsFormData.claimMissingPartnerPoints[ClaimMissingPartnerPointsFormField.ATTACHMENT];
    const file = Array.isArray(attachment) ? attachment.at(0)?.file : attachment?.file;
    if (file) {
      return fileToBase64(file).pipe(
        switchMap((base64: string) => {
          claimPartnerMissingAviosSubmissionData.attachment = base64;
          claimPartnerMissingAviosSubmissionData.attachmentName = file.name;
          return this.sendToClaimMissingPoints(JSON.stringify(claimPartnerMissingAviosSubmissionData));
        })
      );
    } else {
      return this.sendToClaimMissingPoints(JSON.stringify(claimPartnerMissingAviosSubmissionData));
    }
  };

  private sendToClaimMissingPoints = (jsonBody: string): Observable<ClaimMissingAviosResponseData> => {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerCare;
    const httpOptions: HttpPostOptions = {
      headers: new HttpHeaders({ 'x-api-key': keyId }),
    };
    return this.http
      .post<ClaimMissingAviosResponseData>(`${apiUrl}/feedback/claim-missing-points`, jsonBody, httpOptions)
      .pipe(
        catchError((err) => {
          this.sentryLogger.warn(err?.message, { error: err });
          return throwError(() => err);
        })
      );
  };

  private getFlightsMissingAviosSubmissionData = (
    claimFlightsMissingAviosFormData: ClaimFlightsMissingAviosFormData
  ): ClaimFlightsMissingAviosSubmissionData => {
    const claimFlightsMissingAviosSubmissionData = {
      airline: claimFlightsMissingAviosFormData.airline,
      claimMissingPointsForm: ClaimMissingPointsFormOrigin.FROM_FLIGHTS,
      passengerName: claimFlightsMissingAviosFormData.passengerName,
      memberNumber: claimFlightsMissingAviosFormData.memberNumber,
      email: claimFlightsMissingAviosFormData.email,
      phone: claimFlightsMissingAviosFormData.phone,
      message: claimFlightsMissingAviosFormData.message,
      companions: claimFlightsMissingAviosFormData.companions?.map((companion) => ({
        passengerName: companion.companionName,
        memberNumber: companion.companionMemberNumber,
      })),
      locale: this.languageService.langKeyValue,
    } as ClaimFlightsMissingAviosSubmissionData;

    if (claimFlightsMissingAviosFormData.passengerName) {
      claimFlightsMissingAviosSubmissionData.flightDetails = {
        ticketNumber: claimFlightsMissingAviosFormData.ticketNumber || undefined,
        bookingClass: claimFlightsMissingAviosFormData.bookingClass,
        flightFrom: claimFlightsMissingAviosFormData.flightFrom?.title,
        flightTo: claimFlightsMissingAviosFormData.flightTo?.title,
        departureDate: claimFlightsMissingAviosFormData.departureDate?.toString(),
        returnDate: claimFlightsMissingAviosFormData.returnDate?.toString() || undefined,
        flightNumbers: claimFlightsMissingAviosFormData.flightNumbers?.map((flight) => flight.flightNumber),
      };
    } else {
      claimFlightsMissingAviosSubmissionData.ticketNumber = claimFlightsMissingAviosFormData.ticketNumber || undefined;
    }
    return claimFlightsMissingAviosSubmissionData;
  };
}
